import React from "react";
import BannerImg from "../../assets/images/banner/project-management.png";
import EnterpriseServicePage from "../../components/services/enterprise-service-page";

const schemas = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Managed Services",
    serviceType: "Enterprise Managed Services",
    description:
      "Managed services for enterprise software systems, including monitoring, maintenance, upgradation and support.",
    provider: {
      "@type": "Organization",
      name: "Avani Cimcon Technologies",
      url: "https://www.avanicimcon.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.avanicimcon.com/it-consulting-services" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Managed Services",
        item: "https://www.avanicimcon.com/services/managed-services",
      },
    ],
  },
];

const ManagedServices = () => (
  <EnterpriseServicePage
    title="Managed Services for Enterprise Software, Support & Upgradation | Avani Cimcon"
    description="Managed services for enterprise software systems since 1992. Avani Cimcon helps organizations stabilize, support and evolve critical applications without losing the value already built into them."
    keywords="managed services, enterprise managed services, application maintenance, software support, upgradation, enterprise software engineering, legacy modernization, application monitoring, long-term support"
    canonical="https://www.avanicimcon.com/services/managed-services"
    ogImage="https://www.avanicimcon.com/images/banner/project-management.png"
    hero={{
      eyebrow: "Managed Services",
      heading: "Managed Services for enterprise systems that must keep running",
      lead:
        "When business-critical software already works, the right move is often to strengthen it: monitor it, support it, upgrade it and evolve it without disrupting operations.",
      tags: ["Monitoring", "Maintenance", "Upgradation", "Support"],
      primaryCta: { label: "Talk to Us", href: "/contact-us" },
      secondaryCta: { label: "Discuss Your Modernization Roadmap", href: "/services/legacy-modernization" },
      image: BannerImg,
      panelKicker: "Reliability first.",
      panelTitle: "Managed evolution keeps stable systems useful for longer.",
      panelText:
        "Avani Cimcon's managed delivery model fits enterprises that need continuity, governance and a safe path to modernization.",
      stats: [
        { value: "Since 1992", label: "Enterprise delivery history" },
        { value: "30+", label: "Years of software experience" },
        { value: "70+", label: "Countries served" },
      ],
    }}
    whatItIs={{
      eyebrow: "What It Is",
      title: "What managed services mean for enterprise buyers",
      lead: "A managed services model that protects the systems already supporting the business.",
      paragraphs: [
        "Managed services are best when a platform still has business value but needs disciplined ownership. That can include routine support, release management, monitoring, incident response, enhancement planning, application upkeep and controlled upgradation.",
        "At Avani Cimcon, managed services are not presented as a generic outsourcing label. They are part of a broader enterprise engineering model that preserves what works while creating room for modernization, integration and AI enablement.",
      ],
    }}
    whoItIsFor={{
      title: "Who it is for",
      lead: "Organizations that need dependable ownership for systems already embedded in operations.",
      items: [
        "Enterprises with legacy software that still supports critical workflows",
        "Teams that need operational continuity before a larger modernization effort",
        "Businesses that want a long-term engineering partner rather than a short-term vendor",
      ],
    }}
    businessProblems={{
      eyebrow: "Problems Solved",
      title: "The business problems managed services address",
      lead: "Reduce operational drag without forcing a full replacement program.",
      items: [
        {
          title: "Support gaps",
          text: "Fill the ownership gap when internal teams are overloaded or the original system owners are no longer available.",
        },
        {
          title: "Release risk",
          text: "Make changes more safely by using stable delivery processes, testing discipline and controlled deployment planning.",
        },
        {
          title: "Aging systems",
          text: "Keep useful software current through maintenance, upgrade planning and incremental remediation.",
        },
      ],
    }}
    scenarios={{
      eyebrow: "Typical Scenarios",
      title: "Common enterprise situations where managed services help",
      lead: "Real operating conditions that require continuity, not disruption.",
      items: [
        { title: "Mission-critical platform support", text: "Operations teams need the platform to stay available while improvements are planned." },
        { title: "Incremental version upgrades", text: "The business wants new capabilities without replacing the entire application landscape." },
        { title: "Post-project stabilization", text: "A new build or modernization program needs reliable handoff and steady ownership." },
        { title: "Sustained feature evolution", text: "The product or internal system must continue evolving after the initial launch." },
      ],
    }}
    deliveryApproach={{
      eyebrow: "How We Deliver",
      title: "Avani Cimcon's delivery approach for managed services",
      lead: "Support, stabilize and then modernize with measured changes.",
      paragraphs: [
        "We start by understanding the current application landscape, business priorities and operational constraints. From there we define a support model that keeps service levels visible and change risk under control.",
        "That approach works well for enterprise software because it respects the systems already carrying business value. It also creates a pathway into deeper modernization when the organization is ready.",
      ],
      callout: {
        title: "Delivery characteristics",
        items: [
          "24x7 monitoring and response planning",
          "Application maintenance and upgrades",
          "Quality and release discipline",
          "Transition-friendly governance",
        ],
      },
    }}
    industries={{
      eyebrow: "Industries Served",
      title: "Managed services across enterprise verticals",
      lead: "The same continuity model can support several of the company's strongest verticals.",
      items: [
        { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Booking, reservation and operational platforms." },
        { label: "Healthcare", href: "/healthcare", text: "Workflow-heavy systems that need reliability." },
        { label: "Insurance", href: "/insurance", text: "Policy and claims systems with long-lived rules." },
        { label: "Legal", href: "/legal", text: "Document and case workflows that demand precision." },
        { label: "Financial Services", href: "/about", text: "Enterprise systems where continuity and control matter." },
        { label: "DXchange", href: "/dxchange", text: "Integration platforms that connect older and newer systems." },
      ],
    }}
    framework={{
      eyebrow: "Modernization Framework",
      title: "Managed services can be the first step in a safer modernization path",
      lead: "Use the same delivery relationship to preserve value and create change capacity.",
      items: [
        { title: "Assess", text: "Understand the current estate, support pressure and technical risk." },
        { title: "Preserve", text: "Keep the business functions and proven workflows intact." },
        { title: "Stabilize", text: "Reduce incidents, surprises and operational noise." },
        { title: "Modernize", text: "Upgrade pieces of the stack when the timing is right." },
      ],
    }}
    aiOpportunities={{
      eyebrow: "Related AI Opportunities",
      title: "Where managed services open the door to AI enablement",
      lead: "Operational stability makes it easier to adopt AI in a controlled way.",
      items: [
        {
          title: "Enterprise AI Enablement",
          text: "Add intelligence to existing workflows once the support model is stable.",
          href: "/services/enterprise-ai-enablement",
          linkLabel: "Explore AI enablement",
        },
        {
          title: "Enterprise AI Overview",
          text: "See how the company frames AI as a governed layer on top of enterprise software.",
          href: "/enterprise-ai",
          linkLabel: "Open Enterprise AI",
        },
        {
          title: "Legacy Modernization",
          text: "Move from support to modernization with a delivery model already in place.",
          href: "/services/legacy-modernization",
          linkLabel: "See the framework",
        },
      ],
    }}
    relatedProducts={{
      eyebrow: "Related Products",
      title: "Products that reflect the same enterprise delivery discipline",
      lead: "Managed services sits naturally beside platforms the company has already built and supported.",
      items: [
        { label: "DXchange", href: "/dxchange", text: "Enterprise data exchange and integration." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven workflow automation." },
        { label: "CMS", href: "/cms", text: "Content operations and publishing control." },
      ],
    }}
    relatedServices={{
      eyebrow: "Related Services",
      title: "Other service pages that share the same delivery story",
      lead: "Use the service cluster to move from support into modernization and embedded delivery.",
      items: [
        { label: "Dedicated Engineering Teams", href: "/services/dedicated-engineering-teams", text: "Embedded delivery capacity for complex work." },
        { label: "Legacy Modernization", href: "/services/legacy-modernization", text: "Preserve, stabilize and modernize safely." },
        { label: "Enterprise AI Enablement", href: "/services/enterprise-ai-enablement", text: "Add governed AI to existing systems." },
      ],
    }}
    cta={{
      eyebrow: "Talk to Us",
      title: "Need a dependable ownership model for enterprise software?",
      text: "Use managed services to keep the business running while we plan the next stage of evolution together.",
      primary: { label: "Talk to Us", href: "/contact-us" },
      secondary: { label: "Discuss Your Modernization Roadmap", href: "/services/legacy-modernization" },
    }}
    schemas={schemas}
  />
);

export default ManagedServices;
